/**
 * Keyring core — the pure request-handling logic.
 *
 * Extracted from keyring.worker.ts so it can be:
 *   1. Tested directly without spinning up a Worker
 *   2. Used in single-thread/inproc fallback when SharedWorker is unavailable
 *
 * This module has NO references to `self`, `onconnect`, or any worker globals.
 * The worker file becomes thin glue that wires postMessage to `handle()` and
 * subscribes to broadcasts via the exported `subscribeBroadcasts()`.
 *
 * State is module-scoped: there is at most one Keyring instance per JS realm.
 * In a SharedWorker that means one Keyring shared by all tabs; in tests that
 * means one Keyring per test file (vitest isolates test files into separate
 * workers).
 */

import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { blake3 } from '@noble/hashes/blake3';
import { utf8ToBytes } from '@noble/hashes/utils';

import { ActiveIdentityFactory, IDENTITY_MODEL, type SignerIdentity } from '../identity';
import {
  exportBackup as exportBackupBlob,
  importBackup as importBackupBlob,
} from './keyring.backup';
import type {
  KeyringRequest,
  KeyringResponse,
  UnlockRequest,
  GenerateRequest,
  SignRequest,
  VerifyRequest,
  PubkeyRequest,
  LinkageProofRequest,
  DeriveStorageKeyRequest,
  ExportBackupRequest,
  ImportBackupRequest,
} from './keyring.protocol';

// ─────────────────────────────────────────────────────────────────────────────
// STATE
// ─────────────────────────────────────────────────────────────────────────────

interface UnlockedState {
  identity: SignerIdentity;
  /** Root secret bytes, retained so storage keys can be re-derived without
   * re-running Argon2. Wiped on lock. */
  rootMaterial: Uint8Array;
  fingerprint: string;
  unlockedAt: number;
  lastTouchAt: number;
  autoLockMs: number;
  storageKeyCache: Map<string, Uint8Array>;
}

let state: UnlockedState | null = null;
let autoLockTimer: ReturnType<typeof setTimeout> | null = null;

// Track seed across unlock so we can produce a backup later without prompting
// for it again. Wiped on lock.
let lastUnlockSeed: string | null = null;

/** Broadcast subscribers — notified on lock events. */
const broadcastListeners = new Set<(msg: KeyringResponse) => void>();

export function subscribeBroadcasts(fn: (msg: KeyringResponse) => void): () => void {
  broadcastListeners.add(fn);
  return () => broadcastListeners.delete(fn);
}

function broadcast(msg: KeyringResponse): void {
  for (const fn of broadcastListeners) {
    try { fn(msg); } catch { /* listener threw, ignore */ }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// AUTO-LOCK
// ─────────────────────────────────────────────────────────────────────────────

function scheduleAutoLock(): void {
  if (autoLockTimer) clearTimeout(autoLockTimer);
  if (!state || state.autoLockMs <= 0) return;
  const elapsed = Date.now() - state.lastTouchAt;
  const remaining = Math.max(0, state.autoLockMs - elapsed);
  autoLockTimer = setTimeout(() => {
    void lock();
  }, remaining);
}

async function lock(): Promise<void> {
  if (!state) return;
  await state.identity.lock();
  state.rootMaterial.fill(0);
  for (const v of state.storageKeyCache.values()) v.fill(0);
  state.storageKeyCache.clear();
  state = null;
  lastUnlockSeed = null;
  if (autoLockTimer) {
    clearTimeout(autoLockTimer);
    autoLockTimer = null;
  }
  broadcast({ kind: 'lock' });
}

function touch(): number {
  if (!state) return 0;
  state.lastTouchAt = Date.now();
  scheduleAutoLock();
  return state.lastTouchAt;
}

// ─────────────────────────────────────────────────────────────────────────────
// STORAGE KEY DERIVATION
// ─────────────────────────────────────────────────────────────────────────────
// Storage keys are deterministic from root material + domain. Two devices
// unlocked from the same seed derive the same storage keys, which is
// required for IndexedDB replication.

const STORAGE_KEY_DOMAINS = {
  envelope: 'surface-mesh:v1:storage:envelope',
  session: 'surface-mesh:v1:storage:session',
  blob: 'surface-mesh:v1:storage:blob',
} as const;

function deriveStorageKeyBytes(domain: keyof typeof STORAGE_KEY_DOMAINS): Uint8Array {
  if (!state) throw new Error('locked');
  const cached = state.storageKeyCache.get(domain);
  if (cached) return cached;
  const salt = blake3(utf8ToBytes(STORAGE_KEY_DOMAINS[domain]));
  const out = hkdf(
    sha256,
    state.rootMaterial,
    salt,
    utf8ToBytes(STORAGE_KEY_DOMAINS[domain]),
    32,
  );
  state.storageKeyCache.set(domain, out);
  return out;
}

// Re-derives the root material from seed+password. Same Argon2 work as the
// identity factory; this output is kept separately so storage keys can be
// derived without round-tripping through identity internals.
async function deriveRootMaterial(seed: string, password: string): Promise<Uint8Array> {
  // Phase 19.1: WASM argon2id via hash-wasm. ~50-100x faster than the noble
  // pure-JS path, so we can run real OWASP-recommended params (t=3, m=64MB)
  // in <3s on a typical 2024-class device.
  const { argon2id } = await import('hash-wasm');
  const salt = blake3(utf8ToBytes(`surface-mesh-${IDENTITY_MODEL}:v1:${seed}`));
  const out = await argon2id({
    password,
    salt,
    iterations: 3,
    memorySize: 64 * 1024,
    parallelism: 1,
    hashLength: 32,
    outputType: 'binary',
  });
  return hkdf(sha256, out, salt, utf8ToBytes(`${IDENTITY_MODEL}-identity-root`), 32);
}

// ─────────────────────────────────────────────────────────────────────────────
// REQUEST HANDLERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Process one keyring request and produce a response.
 *
 * Throws on protocol errors (locked when unlock required, etc); the worker
 * glue catches and converts to ErrorResponse. Direct callers (tests, inproc
 * fallback) get the exception.
 */
export async function handle(req: KeyringRequest): Promise<KeyringResponse> {
  switch (req.kind) {
    case 'unlock':       return await handleUnlock(req);
    case 'generate':     return await handleGenerate(req);
    case 'lock':         await lock(); return { kind: 'lock' };
    case 'status':       return handleStatus();
    case 'touch':        return { kind: 'touch', lastTouchMs: touch() };
    case 'sign':         return await handleSign(req);
    case 'verify':       return await handleVerify(req);
    case 'pubkey':       return await handlePubkey(req);
    case 'linkage':      return await handleLinkage(req);
    case 'storageKey':   return handleStorageKey(req);
    case 'exportBackup': return await handleExportBackup(req);
    case 'importBackup': return await handleImportBackup(req);
  }
}

async function handleUnlock(req: UnlockRequest): Promise<KeyringResponse> {
  if (state) await lock();
  const identity = await ActiveIdentityFactory.fromSeed(req.seed, req.password);
  state = {
    identity,
    rootMaterial: await deriveRootMaterial(req.seed, req.password),
    fingerprint: identity.fingerprint(),
    unlockedAt: Date.now(),
    lastTouchAt: Date.now(),
    autoLockMs: req.autoLockMs ?? 15 * 60 * 1000,
    storageKeyCache: new Map(),
  };
  lastUnlockSeed = req.seed;
  scheduleAutoLock();
  return {
    kind: 'unlock',
    fingerprint: state.fingerprint,
    identityModel: IDENTITY_MODEL,
  };
}

async function handleGenerate(req: GenerateRequest): Promise<KeyringResponse> {
  if (state) await lock();
  const { identity, seed } = await ActiveIdentityFactory.generate(req.password);
  state = {
    identity,
    rootMaterial: await deriveRootMaterial(seed, req.password),
    fingerprint: identity.fingerprint(),
    unlockedAt: Date.now(),
    lastTouchAt: Date.now(),
    autoLockMs: req.autoLockMs ?? 15 * 60 * 1000,
    storageKeyCache: new Map(),
  };
  lastUnlockSeed = seed;
  scheduleAutoLock();
  return {
    kind: 'generate',
    fingerprint: state.fingerprint,
    seed,
    identityModel: IDENTITY_MODEL,
  };
}

function handleStatus(): KeyringResponse {
  if (!state) {
    return { kind: 'status', unlocked: false, msUntilLock: null };
  }
  const msUntilLock = state.autoLockMs > 0
    ? Math.max(0, state.autoLockMs - (Date.now() - state.lastTouchAt))
    : Infinity;
  return {
    kind: 'status',
    unlocked: true,
    fingerprint: state.fingerprint,
    identityModel: IDENTITY_MODEL,
    msUntilLock,
  };
}

async function handleSign(req: SignRequest): Promise<KeyringResponse> {
  if (!state) throw new Error('locked');
  touch();
  const signature = await state.identity.sign(req.payload, req.context);
  return { kind: 'sign', signature };
}

async function handleVerify(req: VerifyRequest): Promise<KeyringResponse> {
  // Verification doesn't require unlock — pubkey verification is public.
  const identity = state?.identity;
  if (!identity) {
    const { ed25519 } = await import('@noble/curves/ed25519');
    const ok = ed25519.verify(req.signature, req.payload, req.pubkey);
    return { kind: 'verify', ok };
  }
  const ok = await identity.verify(req.payload, req.signature, req.context, req.pubkey);
  return { kind: 'verify', ok };
}

async function handlePubkey(req: PubkeyRequest): Promise<KeyringResponse> {
  if (!state) throw new Error('locked');
  touch();
  const pubkey = await state.identity.pubkey(req.context);
  return { kind: 'pubkey', pubkey };
}

async function handleLinkage(req: LinkageProofRequest): Promise<KeyringResponse> {
  if (!state) throw new Error('locked');
  touch();
  const proof = await state.identity.proveLinkage(req.ctxA, req.ctxB);
  return {
    kind: 'linkage',
    ctxAHash: proof.ctxA,
    ctxBHash: proof.ctxB,
    proof: proof.proof,
    zeroKnowledge: proof.zeroKnowledge,
  };
}

function handleStorageKey(req: DeriveStorageKeyRequest): KeyringResponse {
  if (!state) throw new Error('locked');
  touch();
  // Return a copy — callers (e.g. InprocKeyringClient) zero the bytes after
  // importing the CryptoKey for hygiene. Without the slice() they'd zero the
  // cache too, and subsequent calls would return all-zero "keys".
  const keyBytes = deriveStorageKeyBytes(req.domain).slice();
  return { kind: 'storageKey', keyBytes };
}

async function handleExportBackup(req: ExportBackupRequest): Promise<KeyringResponse> {
  if (!state || !lastUnlockSeed) throw new Error('locked');
  touch();
  const blob = await exportBackupBlob(lastUnlockSeed, state.fingerprint, req.password);
  return { kind: 'exportBackup', blob };
}

async function handleImportBackup(req: ImportBackupRequest): Promise<KeyringResponse> {
  const parsed = await importBackupBlob(req.blob, req.password);
  if (state) await lock();
  const identity = await ActiveIdentityFactory.fromSeed(parsed.seed, req.password);
  state = {
    identity,
    rootMaterial: await deriveRootMaterial(parsed.seed, req.password),
    fingerprint: identity.fingerprint(),
    unlockedAt: Date.now(),
    lastTouchAt: Date.now(),
    autoLockMs: req.autoLockMs ?? 15 * 60 * 1000,
    storageKeyCache: new Map(),
  };
  lastUnlockSeed = parsed.seed;
  scheduleAutoLock();
  return { kind: 'importBackup', fingerprint: state.fingerprint };
}

// ─────────────────────────────────────────────────────────────────────────────
// TEST HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reset all keyring state. For tests only — do NOT call from app code.
 *
 * Equivalent to a synchronous lock + clear, used between test cases so
 * module-scoped state from prior tests doesn't bleed in.
 */
export async function __resetForTests(): Promise<void> {
  if (state) {
    await state.identity.lock();
    state.rootMaterial.fill(0);
    for (const v of state.storageKeyCache.values()) v.fill(0);
    state.storageKeyCache.clear();
  }
  state = null;
  lastUnlockSeed = null;
  if (autoLockTimer) {
    clearTimeout(autoLockTimer);
    autoLockTimer = null;
  }
  broadcastListeners.clear();
}
